// Render del configure panel cuando el preset es 'anuncio'. Muestra:
//   • Card de Productos (chip + clear) — igual al de DesignModeloPanel.
//   • 3 cards de campos textuales (Título / Descripción / Llamada a
//     acción), cada una con un tri-toggle Auto / Personalizado / Quitar.
//     Cuando el toggle está en 'custom' el body de la card muestra el
//     input/textarea; en 'auto' o 'hidden' muestra un mensaje gris.
//   • Card de Colores — grilla de hasta 4 swatches; los slots vacíos son
//     placeholders con "+", los llenos muestran el hex como label flot.
//     Si el user no editó nada, el panel cae al `brandColors` recibido.
//   • Card de Opciones (Formato + textarea Instrucción adicional) —
//     mismo patrón que el resto de modos.
//
// El botón Generar lo renderiza el ConfigurePanel padre al final del
// flujo, no este panel.

import React, { useEffect, useRef, useState } from 'react';
import {
  ALargeSmall,
  Check,
  ChevronDown,
  ChevronRight,
  Eye,
  EyeOff,
  X,
} from 'lucide-react';
import type { MockProductV2 } from '../mockData';
import { TEXT_FONT_OPTIONS, fontStackFor } from '../../../textFonts';
import CachedImg from '../../../components/CachedImg';

// ─── Tipos compartidos ────────────────────────────────────────────────
// Cada campo textual del anuncio tiene 2 dimensiones:
//   • `hidden`  — el user descartó el campo (icono ojo cerrado). Cuando
//                 es true la row se ve gris y no va al anuncio final.
//   • `value`   — el texto escrito por el user. Si está vacío Y `hidden`
//                 es false, queda implícito el modo "automático" — Atlas
//                 decide el contenido. Si tiene texto, es "custom".
// El modo (auto/custom) se deriva del `value`, no lo guardamos aparte.
//   • `font`    — familia tipográfica elegida para este texto. Cadena
//                 vacía '' = "Automática" (la IA elige la fuente). Si no,
//                 una familia de TEXT_FONT_OPTIONS (las mismas del editor
//                 de video). Se manda al backend como `fuente`.
export interface AnuncioFieldState {
  hidden: boolean;
  value: string;
  font: string;
}

export const ANUNCIO_COLORS_MAX = 4;

// ─── Helpers de marca (colores + fuente) ──────────────────────────────
// Los colores de marca (`brand_profiles.colors`) llegan en DOS formas: el
// editor guarda `string[]` de hex, y Estudio Virtual guarda
// `{ hex, name, role }[]`. Además algunas marcas guardaron el hex SIN el
// `#` (ej. "BCA991") o en formato corto (`#abc`). El `<input type="color">`
// nativo SOLO acepta `#rrggbb`, así que normalizamos al leer: extraemos el
// hex, agregamos `#`, expandimos 3→6 dígitos y validamos. Lo que no se
// puede normalizar se descarta.
const normalizeHex = (value: unknown): string | null => {
  if (typeof value !== 'string') return null;
  let h = value.trim();
  if (!h) return null;
  if (h[0] === '#') h = h.slice(1);
  const body = h.replace(/[^0-9a-fA-F]/g, '');
  if (body.length === 3) {
    return (
      '#' +
      body
        .split('')
        .map((c) => c + c)
        .join('')
    ).toLowerCase();
  }
  if (body.length === 6 || body.length === 8) {
    // 8 dígitos = hex con alpha; el picker nativo no soporta alpha → 6.
    return ('#' + body.slice(0, 6)).toLowerCase();
  }
  return null;
};

// Normaliza la paleta del brand a hex válidos (`#RRGGBB`) listos para el
// color picker. Acepta `string[]` o `{ hex }[]`. Exportado para que
// InterfazV2 lo use al leer `brand_profiles.colors`.
export const normalizeBrandHexColors = (raw: unknown): string[] => {
  if (!Array.isArray(raw)) return [];
  const out: string[] = [];
  for (const c of raw) {
    const hex =
      typeof c === 'string'
        ? c
        : c &&
            typeof c === 'object' &&
            typeof (c as { hex?: unknown }).hex === 'string'
          ? (c as { hex: string }).hex
          : null;
    const norm = normalizeHex(hex);
    if (norm) out.push(norm);
  }
  return out;
};

// Resuelve la fuente de marca por defecto: la primera de
// `brand_profiles.fonts.suggested` que sea RENDERIZABLE (esté en
// TEXT_FONT_OPTIONS). Si ninguna lo es (ej. "Plus Jakarta Sans"), devuelve
// '' → el campo queda en "Automática" (la IA decide). Devuelve la familia
// con el casing canónico de TEXT_FONT_OPTIONS para matchear el dropdown.
export const resolveBrandFont = (fonts: unknown): string => {
  if (!fonts || typeof fonts !== 'object') return '';
  const suggested = (fonts as { suggested?: unknown }).suggested;
  if (!Array.isArray(suggested)) return '';
  for (const f of suggested) {
    if (typeof f !== 'string') continue;
    const target = f.trim().toLowerCase();
    const opt = TEXT_FONT_OPTIONS.find((o) => o.family.toLowerCase() === target);
    if (opt) return opt.family;
  }
  return '';
};

// ─── Sub: selector de fuente por texto ────────────────────────────────
// Dropdown compacto reutilizando la lista del editor de video. '' =
// Automática (la IA elige). Cada opción se renderiza en su propia
// tipografía para previsualizarla. Cierra al clickear afuera o elegir.
const FontSelect: React.FC<{
  value: string;
  onChange: (next: string) => void;
}> = ({ value, onChange }) => {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement | null>(null);
  useEffect(() => {
    if (!open) return;
    const onDoc = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, [open]);
  return (
    <div ref={ref} className="flex items-center gap-2.5">
      {/* Label "Fuente" a la IZQUIERDA del dropdown: ícono "Aa" + texto
          gris, al mismo tamaño que el resto del contenido de la fila. */}
      <span className="inline-flex items-center gap-1.5 text-[14px] text-[#3C3C43]/55 shrink-0">
        <ALargeSmall className="w-[18px] h-[18px]" strokeWidth={2} />
        Fuente
      </span>
      <div className="relative flex-1 min-w-0">
        <button
          type="button"
          onClick={() => setOpen((v) => !v)}
          className="w-full h-10 px-3.5 inline-flex items-center justify-between gap-2 rounded-xl bg-white border border-neutral-200 hover:border-brand-primary/40 focus:outline-none focus:ring-2 focus:ring-brand-primary/30 transition-colors"
        >
          <span
            className="text-[14px] text-brand-black/90 truncate"
            style={{ fontFamily: value ? fontStackFor(value) : undefined }}
          >
            {value || 'Automática'}
          </span>
          <ChevronDown
            className={`w-4 h-4 text-[#3C3C43]/55 shrink-0 transition-transform duration-200 ${
              open ? 'rotate-180' : ''
            }`}
          />
        </button>
        {open && (
          <div className="absolute z-20 mt-1 left-0 right-0 max-h-64 overflow-y-auto rounded-xl bg-white border border-neutral-200 shadow-lg py-1">
            {['', ...TEXT_FONT_OPTIONS.map((o) => o.family)].map((family) => {
              const isAuto = family === '';
              const selected = family === value;
              return (
                <button
                  key={family || '__auto__'}
                  type="button"
                  onClick={() => {
                    onChange(family);
                    setOpen(false);
                  }}
                  className={`w-full px-3.5 py-2 inline-flex items-center justify-between gap-2 text-left hover:bg-black/[0.04] transition-colors ${
                    selected ? 'bg-brand-primary/[0.06]' : ''
                  }`}
                >
                  <span
                    className="text-[14px] text-brand-black/90 truncate"
                    style={{
                      fontFamily: isAuto ? undefined : fontStackFor(family),
                    }}
                  >
                    {isAuto ? 'Automática' : family}
                  </span>
                  {selected && (
                    <Check className="w-4 h-4 text-brand-primary shrink-0" />
                  )}
                </button>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

// ─── Sub: fila de campo textual (estilo acordeón) ─────────────────────
// Patrón visual:
//   • Header clickeable — chevron (rota 90° al abrir) + label a la
//     izquierda; botón "ojo" right-aligned para toggle hidden.
//   • Cuando `expanded` y NO `hidden`, debajo del header aparece el
//     input/textarea + un hint italic ("Atlas decide si lo dejás vacío").
//   • Cuando `hidden` la row entera se ve gris (opacity-50) — sigue
//     visible para que el user pueda re-habilitar tocando el ojo, pero
//     se entiende como "descartada".
const FieldRow: React.FC<{
  label: string;
  placeholder: string;
  multiline?: boolean;
  state: AnuncioFieldState;
  onChange: (next: AnuncioFieldState) => void;
}> = ({ label, placeholder, multiline = false, state, onChange }) => {
  const [expanded, setExpanded] = useState(false);
  const inputRef = useRef<HTMLTextAreaElement | HTMLInputElement | null>(null);
  const { hidden, value } = state;
  // Auto-expand cuando dejás de estar hidden si tenés texto — así si
  // re-habilitás un campo personalizado se ve el texto guardado.
  // (No auto-collapse al hide, queda al gusto del user.)
  return (
    <div
      className={`flex flex-col transition-opacity duration-200 ${
        hidden ? 'opacity-50' : 'opacity-100'
      }`}
    >
      <div className="flex items-center justify-between gap-2 py-2">
        <button
          type="button"
          onClick={() => setExpanded((v) => !v)}
          disabled={hidden}
          className="flex-1 inline-flex items-center gap-2 text-left disabled:cursor-not-allowed"
        >
          <ChevronRight
            className={`w-4 h-4 text-[#3C3C43]/55 shrink-0 transition-transform duration-200 ${
              expanded && !hidden ? 'rotate-90' : ''
            }`}
            strokeWidth={2.5}
          />
          <span className="text-[15px] font-medium text-brand-black/90 leading-snug">
            {label}
          </span>
        </button>
        <button
          type="button"
          onClick={() => onChange({ ...state, hidden: !hidden })}
          aria-label={hidden ? 'Mostrar' : 'Ocultar'}
          className="w-8 h-8 inline-flex items-center justify-center rounded-full text-[#3C3C43]/55 hover:text-brand-black/80 hover:bg-black/[0.04] transition-colors shrink-0"
        >
          {hidden ? (
            <EyeOff className="w-[18px] h-[18px]" />
          ) : (
            <Eye className="w-[18px] h-[18px]" />
          )}
        </button>
      </div>
      {/* Body: input + hint cuando expanded && !hidden. Sin indent
          izquierdo — el input usa el ancho completo de la sección para
          que los bordes izquierdo y derecho queden simétricos respecto
          del padding del card. */}
      {expanded && !hidden && (
        <div className="pb-2 flex flex-col gap-2 animate-mode-swap">
          {multiline ? (
            <textarea
              ref={inputRef as React.Ref<HTMLTextAreaElement>}
              value={value}
              onChange={(e) => onChange({ ...state, value: e.target.value })}
              placeholder={placeholder}
              rows={3}
              className="w-full px-3.5 py-2.5 rounded-xl bg-white border border-neutral-200 text-[14px] text-brand-black/90 placeholder:text-[#3C3C43]/40 focus:outline-none focus:ring-2 focus:ring-brand-primary/30 focus:border-brand-primary transition-colors resize-none"
            />
          ) : (
            <input
              ref={inputRef as React.Ref<HTMLInputElement>}
              type="text"
              value={value}
              onChange={(e) => onChange({ ...state, value: e.target.value })}
              placeholder={placeholder}
              className="w-full h-10 px-3.5 rounded-xl bg-white border border-neutral-200 text-[14px] text-brand-black/90 placeholder:text-[#3C3C43]/40 focus:outline-none focus:ring-2 focus:ring-brand-primary/30 focus:border-brand-primary transition-colors"
            />
          )}
          {!value.trim() && (
            <p className="text-[12px] text-[#3C3C43]/55 italic">
              Si lo dejás vacío Atlas elige el texto según el producto y la marca.
            </p>
          )}
          <FontSelect
            value={state.font || ''}
            onChange={(font) => onChange({ ...state, font })}
          />
        </div>
      )}
    </div>
  );
};

// ─── Sub: grilla de colores ──────────────────────────────────────────
// 4 slots side-by-side. Los slots CON color son un `<label>` que envuelve
// un `<input type="color">` transparente y clickeable (mismo patrón que
// "Mi diseño"/CompanyData): el picker nativo se abre con el GESTO real del
// usuario y se cierra/comporta bien (+ hex label + X para quitar). Antes se
// disparaba con `.click()` sintético + `requestAnimationFrame` sobre un
// input `pointer-events-none`, lo que dejaba el picker "trabado".
// Los slots VACÍOS son un "+" (botón) que agrega un color por defecto; se
// edita tocándolo. No llevan color input (ver handleAdd).
const ColorGrid: React.FC<{
  colors: string[];
  onChange: (next: string[]) => void;
  brandDefaults: string[];
}> = ({ colors, onChange, brandDefaults }) => {
  // Si el user no editó nada, mostramos los primeros 4 del brand.
  // Apenas toca algo, partimos del default actual y la edición pasa a
  // ser persistente (sale del fallback).
  const effective =
    colors.length > 0 ? colors : brandDefaults.slice(0, ANUNCIO_COLORS_MAX);
  const isFallback = colors.length === 0 && brandDefaults.length > 0;

  // Array base a mutar: si estamos mostrando el fallback de marca, lo
  // "realizamos" en un array concreto al primer cambio.
  const baseArray = () =>
    isFallback ? brandDefaults.slice(0, ANUNCIO_COLORS_MAX) : effective.slice();

  // El user editó el color de un slot existente.
  const handleSlotChange = (idx: number, hex: string) => {
    const baseline = baseArray();
    while (baseline.length <= idx) baseline.push('#000000');
    baseline[idx] = normalizeHex(hex) ?? hex.toLowerCase();
    onChange(baseline);
  };

  // El user toca "+" → agrega un color por defecto al final y lo edita
  // tocándolo (slot lleno). NO usamos un `<input type=color>` en el slot
  // vacío: dispara onChange por cada micro-movimiento del picker y, al
  // re-renderizar el slot a "lleno", el picker se cerraría de golpe (mismo
  // motivo por el que "Mi diseño" agrega el color con un botón). Default =
  // el próximo color de marca no usado; si no queda, un gris neutro.
  const handleAdd = () => {
    const baseline = baseArray();
    if (baseline.length >= ANUNCIO_COLORS_MAX) return;
    const used = baseline.map((c) => c.toLowerCase());
    const nextBrand = brandDefaults
      .map((c) => c.toLowerCase())
      .find((c) => !used.includes(c));
    baseline.push(nextBrand ?? '#888888');
    onChange(baseline);
  };

  // El user removió un slot.
  const handleRemove = (idx: number) => {
    onChange(baseArray().filter((_, i) => i !== idx));
  };

  return (
    <div className="flex flex-col gap-3">
      <div className="grid grid-cols-4 gap-2.5">
        {Array.from({ length: ANUNCIO_COLORS_MAX }).map((_, i) => {
          const color = effective[i];
          if (color) {
            // `safe` garantiza un `#rrggbb` válido para el input nativo
            // (los colores de marca pueden venir sin `#` o en 3 dígitos).
            const safe = normalizeHex(color) ?? '#000000';
            return (
              <div
                key={i}
                className="relative aspect-square rounded-xl overflow-hidden ring-1 ring-black/[0.06] group"
              >
                <label
                  className="absolute inset-0 block cursor-pointer"
                  style={{ backgroundColor: safe }}
                  aria-label={`Cambiar color ${safe}`}
                >
                  <input
                    type="color"
                    value={safe}
                    onChange={(e) => handleSlotChange(i, e.target.value)}
                    className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
                  />
                </label>
                <button
                  type="button"
                  onClick={() => handleRemove(i)}
                  aria-label="Quitar color"
                  className="absolute top-1 right-1 z-10 w-5 h-5 inline-flex items-center justify-center rounded-full bg-black/55 backdrop-blur-sm text-white opacity-0 group-hover:opacity-100 transition-opacity"
                >
                  <X className="w-3 h-3" strokeWidth={3} />
                </button>
                <span className="pointer-events-none absolute bottom-1 left-1 right-1 inline-flex items-center justify-center rounded-md bg-white/85 backdrop-blur-sm px-1.5 py-0.5 text-[10.5px] font-medium text-brand-black/85 uppercase tabular-nums truncate">
                  {safe.replace('#', '')}
                </span>
              </div>
            );
          }
          // Slot vacío — "+". Agrega un color por defecto (botón, sin color
          // input) y el user lo edita tocándolo. Ver comentario de handleAdd.
          return (
            <button
              key={i}
              type="button"
              onClick={() => handleAdd()}
              className="aspect-square rounded-xl border-2 border-dashed border-neutral-300 hover:border-brand-primary/40 bg-white hover:bg-brand-primary/[0.02] flex items-center justify-center text-[#3C3C43]/55 hover:text-brand-primary transition-colors"
              aria-label="Agregar color"
            >
              <span className="text-[22px] leading-none">+</span>
            </button>
          );
        })}
      </div>
      {isFallback && (
        <p className="text-[12px] text-[#3C3C43]/55 leading-snug">
          Usamos los colores guardados en tu marca. Tocá un color para
          modificarlo.
        </p>
      )}
    </div>
  );
};

// ─── AnuncioPanel ─────────────────────────────────────────────────────
export interface AnuncioPanelProps {
  variant: 'mobile' | 'desktop';
  /** Productos base del anuncio. Opcionales: si no se pasan (o está
   *  vacío) NO se renderiza la sección "Productos" — ej. al convertir una
   *  imagen generada en anuncio, donde la base es la imagen, no un producto. */
  products?: MockProductV2[];
  onClearProducts?: () => void;
  // ── Campos textuales ──
  titulo: AnuncioFieldState;
  onTituloChange: (next: AnuncioFieldState) => void;
  descripcion: AnuncioFieldState;
  onDescripcionChange: (next: AnuncioFieldState) => void;
  cta: AnuncioFieldState;
  onCtaChange: (next: AnuncioFieldState) => void;
  // ── Colores ──
  colors: string[];
  onColorsChange: (next: string[]) => void;
  brandColors: string[];
  // ── Cantidad de imágenes ──
  /** Cantidad de imágenes a generar (1..autoImageCountMax). Default 1.
   *  Mismo state que comparte el modo design-auto en InterfazV2. */
  autoImageCount: number;
  onAutoImageCountChange: (v: number) => void;
  autoImageCountMax: number;
  // ── Formato + Instrucción adicional ──
  formatoLabel: string;
  onOpenFormat: () => void;
  otraConfig: string;
  onOtraChange: (v: string) => void;
  /** Títulos de sección compactos (chico/gris, como "Imagen de
   *  referencia") + descripciones ocultas. 'mobile' = sólo en mobile
   *  (desktop normal); 'always' = en todos los breakpoints. undefined =
   *  off (títulos grandes + descripciones, modo anuncio normal). Cuando
   *  está activo, la sección "Productos" muestra el chip gris standalone
   *  (sin card ni título) en mobile. */
  minimalHeaders?: 'mobile' | 'always';
}

export const AnuncioPanel: React.FC<AnuncioPanelProps> = ({
  variant,
  products,
  onClearProducts,
  titulo,
  onTituloChange,
  descripcion,
  onDescripcionChange,
  cta,
  onCtaChange,
  colors,
  onColorsChange,
  brandColors,
  autoImageCount,
  onAutoImageCountChange,
  autoImageCountMax,
  formatoLabel,
  onOpenFormat,
  otraConfig,
  onOtraChange,
  minimalHeaders,
}) => {
  // Dropdown abierto del selector "Cantidad" — mismo patrón que
  // DesignModeloPanel (en design-auto). Cierra al elegir o tap-fuera.
  const [autoCountOpen, setAutoCountOpen] = useState<boolean>(false);
  const autoCountRef = useRef<HTMLDivElement | null>(null);
  useEffect(() => {
    if (!autoCountOpen) return;
    const handleClick = (e: MouseEvent) => {
      if (!autoCountRef.current?.contains(e.target as Node)) {
        setAutoCountOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [autoCountOpen]);
  const autoCountLabel = `${autoImageCount} ${
    autoImageCount === 1 ? 'imagen' : 'imágenes'
  }`;
  // Layout vertical en ambos viewports — el aside donde vive el panel
  // es estrecho (~420px) tanto en desktop como en mobile sheet, así que
  // un cabezal lateral fijo (estilo DesignModeloPanel) recorta el
  // contenido. Siempre apilamos label + descripción arriba y el
  // contenido full-width abajo.
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  void variant;
  // `minimalHeaders`: título compacto gris en MOBILE y negro/normal en
  // DESKTOP; descripciones ocultas ('always' en todos los breakpoints,
  // 'mobile' sólo en mobile). `bareOnMobile`: en mobile la sección pierde
  // la card y el header (su contenido queda suelto); en desktop sigue
  // siendo card.
  const sectionWrap = (
    title: string,
    description: string,
    content: React.ReactNode,
    bareOnMobile?: boolean,
  ) => {
    const bare = !!bareOnMobile && !!minimalHeaders;
    return (
      <section
        className={
          bare
            ? 'flex flex-col gap-3 md:rounded-2xl md:bg-white md:border md:border-neutral-200 md:p-5'
            : 'rounded-2xl bg-white border border-neutral-200 p-4 md:p-5 flex flex-col gap-3'
        }
      >
        <div className={bare ? 'hidden md:block' : ''}>
          {minimalHeaders ? (
            <>
              {/* Mobile: mismo tamaño y grosor que los labels (ej. "Cantidad"). */}
              <h3 className="md:hidden text-[13.5px] text-brand-black/85 leading-tight">
                {title}
              </h3>
              {/* Desktop: negro/normal. (Dos h3 separados para evitar que el
                  override de color responsive no aplique por orden de Tailwind.) */}
              <h3 className="hidden md:block text-[17px] font-semibold text-brand-black/90 leading-tight">
                {title}
              </h3>
            </>
          ) : (
            <h3 className="text-[16px] md:text-[17px] font-semibold text-brand-black/90 leading-tight">
              {title}
            </h3>
          )}
          <p
            className={`text-[12.5px] text-[#3C3C43]/60 mt-1 leading-snug ${
              minimalHeaders === 'always'
                ? 'hidden'
                : minimalHeaders === 'mobile'
                  ? 'hidden md:block'
                  : ''
            }`}
          >
            {description}
          </p>
        </div>
        <div className="min-w-0">{content}</div>
      </section>
    );
  };

  // Chip gris del/los producto(s). Se reusa con o sin la card de sección
  // "Productos" según `minimalHeaders` (en mobile va standalone).
  const productosChip =
    products && products.length > 0 ? (
      <div className="flex items-center gap-3 bg-[#F7F7F9] rounded-2xl p-3">
        {products.length === 1 ? (
          <CachedImg
            src={products[0].imageUrl}
            alt={products[0].name}
            className="w-12 h-12 rounded-lg object-cover shrink-0"
          />
        ) : (
          <div className="flex shrink-0 -space-x-2">
            {products.slice(0, 3).map((p) => (
              <CachedImg
                key={p.id}
                src={p.imageUrl}
                alt={p.name}
                className="w-10 h-10 rounded-lg object-cover ring-2 ring-[#F7F7F9]"
              />
            ))}
            {products.length > 3 && (
              <div className="w-10 h-10 rounded-lg bg-white ring-2 ring-[#F7F7F9] flex items-center justify-center text-[12px] font-semibold text-brand-black/70">
                +{products.length - 3}
              </div>
            )}
          </div>
        )}
        <p className="flex-1 min-w-0 text-[14.5px] font-semibold text-brand-black/90 truncate">
          {products.length === 1
            ? products[0].name
            : products.map((p) => p.name).join(', ')}
        </p>
        <button
          type="button"
          onClick={() => onClearProducts?.()}
          className="w-8 h-8 inline-flex items-center justify-center rounded-full text-neutral-400 hover:text-neutral-700 hover:bg-black/[0.04] transition-colors shrink-0"
          aria-label="Cambiar selección"
        >
          <X className="w-4 h-4" />
        </button>
      </div>
    ) : null;

  return (
    <div className="flex flex-col gap-4">
      {/* Productos seleccionados. Con `minimalHeaders` (modo anuncio): en
          mobile el chip gris va standalone (sin card ni título "Productos");
          en desktop mantiene la card de sección. Sin el flag: card normal. */}
      {products &&
        products.length > 0 &&
        (minimalHeaders ? (
          <>
            <div className="md:hidden">{productosChip}</div>
            <div className="hidden md:block">
              {sectionWrap(
                'Productos',
                'El anuncio se va a armar alrededor de estos productos.',
                productosChip,
              )}
            </div>
          </>
        ) : (
          sectionWrap(
            'Productos',
            'El anuncio se va a armar alrededor de estos productos.',
            productosChip,
          )
        ))}

      {/* 3 campos textuales como acordeón inline (sin card wrapper).
          Header con chevron rotativo + ojo right-aligned. Por default
          arrancan cerrados y "automáticos" (vacíos). El ojo descarta
          el campo del anuncio sin quitarlo de la UI. */}
      {sectionWrap(
        'Textos',
        'El asistente arma cada texto solo, salvo que lo personalices o lo quites con el ojo.',
        <div className="flex flex-col divide-y divide-black/[0.06]">
          <FieldRow
            label="Título"
            placeholder="Ej: Vení a probar nuestra nueva colección"
            state={titulo}
            onChange={onTituloChange}
          />
          <FieldRow
            label="Descripción"
            placeholder="Ej: Ofertas hasta el 30/06, productos seleccionados"
            multiline
            state={descripcion}
            onChange={onDescripcionChange}
          />
          <FieldRow
            label="Llamada a acción"
            placeholder="Ej: Comprá ahora · Ver más · Reservar"
            state={cta}
            onChange={onCtaChange}
          />
        </div>,
      )}

      {/* Colores (hasta 4). */}
      {sectionWrap(
        'Colores',
        'Hasta 4 colores que van a guiar la paleta del anuncio.',
        <ColorGrid
          colors={colors}
          onChange={onColorsChange}
          brandDefaults={brandColors}
        />,
      )}

      {/* Cantidad + Formato + Instrucción adicional. */}
      {sectionWrap(
        'Opciones',
        'Definí cuántas imágenes querés generar, el formato de salida y agregá indicaciones extras para afinar el resultado.',
        <div className="flex flex-col gap-2.5">
          {/* Selector Cantidad — mismo patrón que el de design-auto:
              row con label izquierda + valor + chevron, dropdown
              absolute con los valores 1..autoImageCountMax. */}
          <div ref={autoCountRef} className="relative">
            <button
              type="button"
              onClick={() => setAutoCountOpen((v) => !v)}
              aria-haspopup="listbox"
              aria-expanded={autoCountOpen}
              className="w-full flex items-center justify-between px-4 py-3 rounded-2xl bg-white border border-neutral-200 hover:border-brand-primary/40 active:bg-neutral-50 transition-colors"
            >
              <span className="text-[13.5px] text-brand-black/85">
                Cantidad
              </span>
              <span className="inline-flex items-center gap-1.5 text-[13.5px] font-medium text-brand-black/85">
                {autoCountLabel}
                <ChevronDown
                  className={`w-3.5 h-3.5 text-[#3C3C43]/55 transition-transform ${
                    autoCountOpen ? 'rotate-180' : ''
                  }`}
                />
              </span>
            </button>
            {autoCountOpen && (
              <ul
                role="listbox"
                aria-label="Cantidad de imágenes"
                className="absolute left-0 right-0 top-[calc(100%+4px)] z-30 bg-white border border-neutral-200 rounded-2xl shadow-lg overflow-hidden animate-mode-swap"
              >
                {Array.from(
                  { length: autoImageCountMax },
                  (_, i) => i + 1,
                ).map((n) => {
                  const selected = n === autoImageCount;
                  const label = `${n} ${n === 1 ? 'imagen' : 'imágenes'}`;
                  return (
                    <li key={n}>
                      <button
                        type="button"
                        role="option"
                        aria-selected={selected}
                        onClick={() => {
                          onAutoImageCountChange(n);
                          setAutoCountOpen(false);
                        }}
                        className={`w-full text-left px-4 py-2.5 text-[13.5px] transition-colors flex items-center justify-between ${
                          selected
                            ? 'bg-brand-primary/[0.07] text-brand-primary font-semibold'
                            : 'text-brand-black/85 hover:bg-neutral-50'
                        }`}
                      >
                        <span>{label}</span>
                        {selected && (
                          <Check className="w-3.5 h-3.5" strokeWidth={3} />
                        )}
                      </button>
                    </li>
                  );
                })}
              </ul>
            )}
          </div>
          <button
            type="button"
            onClick={onOpenFormat}
            className="w-full flex items-center justify-between px-4 py-3 rounded-2xl bg-white border border-neutral-200 hover:border-brand-primary/40 active:bg-neutral-50 transition-colors"
          >
            <span className="text-[13.5px] text-brand-black/85">
              Formato
            </span>
            <span className="text-[13.5px] font-medium text-brand-black/85">
              {formatoLabel}
            </span>
          </button>
          <textarea
            value={otraConfig}
            onChange={(e) => onOtraChange(e.target.value)}
            placeholder="Instrucción adicional"
            rows={2}
            className="w-full px-4 py-3 rounded-2xl bg-white border border-neutral-200 text-[14.5px] text-brand-black/90 placeholder:text-[#3C3C43]/40 focus:outline-none focus:ring-2 focus:ring-brand-primary/30 focus:border-brand-primary transition-colors resize-none"
          />
        </div>,
        true,
      )}
    </div>
  );
};
